import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Image, Platform, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { hideChefToast, selectChefToast } from '../redux/uiSlice';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { Paprika } from '../styles/theme';

/** Long enough to read a recipe title twice; the cook is already somewhere else. */
export const CHEF_TOAST_MS = 3500;

const NATIVE = Platform.OS !== 'web';
const CHEF = require('../../assets/brand/pastille/pastille-64.png');
const EASE_OUT = Easing.bezier(0.22, 0.7, 0.25, 1);

/**
 * Du Chef saying that something worked, drawn by the (app) layout so it
 * survives the navigation that usually follows (#793 — a deleted recipe sent
 * the cook back with no word that it was gone). The roundel pops in with a
 * small nod and a sage check; under reduced motion it only fades.
 */
const ChefToast: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useSelector(selectChefToast);
  const reduced = useReduceMotion();
  const shown = useRef(new Animated.Value(0)).current;
  const chef = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!toast) return;
    shown.setValue(0);
    chef.setValue(reduced ? 1 : 0);
    Animated.timing(shown, { toValue: 1, duration: 220, easing: EASE_OUT, useNativeDriver: NATIVE }).start();
    if (!reduced) {
      Animated.spring(chef, { toValue: 1, friction: 4, tension: 120, useNativeDriver: NATIVE }).start();
    }
    const key = toast.key;
    const timer = setTimeout(() => {
      Animated.timing(shown, { toValue: 0, duration: 180, useNativeDriver: NATIVE })
        .start(() => dispatch(hideChefToast(key)));
    }, CHEF_TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast?.key]);

  if (!toast) return null;

  const rise = shown.interpolate({ inputRange: [0, 1], outputRange: [reduced ? 0 : 16, 0] });
  const scale = chef.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] });
  const rotate = chef.interpolate({ inputRange: [0, 1], outputRange: ['-14deg', '0deg'] });

  return (
    <View style={styles.container} pointerEvents="none">
      <Animated.View
        style={[styles.toast, { opacity: shown, transform: [{ translateY: rise }] }]}
        testID="chef-toast"
        accessibilityLiveRegion="polite"
        accessibilityRole="alert"
      >
        <Animated.View style={[styles.chef, { transform: [{ scale }, { rotate }] }]}>
          <Image source={CHEF} style={styles.chefImage} accessibilityIgnoresInvertColors />
          <View style={styles.check}>
            <Feather name="check" size={10} color={Paprika.onPrimary} />
          </View>
        </Animated.View>
        <Text style={styles.message} numberOfLines={3}>{toast.message}</Text>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 90,
    left: 16,
    right: 16,
    alignItems: 'center',
    zIndex: 9999,
  },
  toast: {
    width: '100%',
    maxWidth: 420,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
    shadowColor: 'rgba(82, 68, 60, 1)',
    shadowOpacity: 0.16,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  chef: {
    width: 40,
    height: 40,
  },
  chefImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  check: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.approvalYes,
    borderWidth: 2,
    borderColor: Paprika.surfaceLowest,
  },
  message: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textPrimary,
  },
});

export default ChefToast;
